import AgoraRTC, {
  CameraVideoTrackInitConfig,
  ClientConfig,
  ConnectionDisconnectedReason,
  ConnectionState,
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  ILocalAudioTrack,
  ILocalTrack,
  ILocalVideoTrack,
  IMicrophoneAudioTrack,
  NetworkQuality,
  UID
} from 'agora-rtc-sdk-ng'
import EventEmitter from 'events'
import { toast } from 'react-hot-toast'

import { ActMediaType, ChangeCamera, ChangeMicrophone, Correct, CorrectType, MediaType, PlayConf } from '.'
import FabricTrack from './fabricTrack'

export interface RtcOptions {
  channel: string
  uid: number
  token?: string
  viceToken?: string
}

export interface VolumeType {
  level: number
  uid: UID
}

export type RemoteNetworkQuality = Record<string, NetworkQuality>
export interface Network {
  local: NetworkQuality
  remote: RemoteNetworkQuality
}

export const networkQualityEnum = {
  6: 0,
  5: 1,
  4: 2,
  3: 2,
  2: 3,
  1: 3,
  0: 0
}

/**
 * 采集分辨率，可以提为入参
 * 高度不够，上下留黑
 * 高度正常，宽度超出，左右截取
 * 宽度不够，宽缩放至100%，高等比缩放，上下留黑，高度不会裁剪
 */
const videSize = {
  width: 1280, // 4:3
  height: 960
}

const cameraVideoTrackInitConfig: CameraVideoTrackInitConfig = {
  encoderConfig: {
    bitrateMax: 960,
    bitrateMin: 960,
    frameRate: 15,
    width: {
      max: videSize.width,
      ideal: videSize.width
    },
    height: {
      max: videSize.height,
      ideal: videSize.height
    }
  },
  facingMode: 'user',
  optimizationMode: 'detail'
}

const clientConfig: ClientConfig = { mode: 'live', codec: 'vp8', role: 'host' }

export default class Agora extends EventEmitter {
  private appId: string = import.meta.env.VITE_APP_AGORA_APPID
  private channel: string = ''
  private token: string | null = null
  private viceToken: string | null = null
  private uid: number = 0
  private mainUid: number = 0
  private viceUid: number = 0
  public mainFabricTrack: FabricTrack | null = null // fabricTrack 自定义摄像头
  public viceFabricTrack: FabricTrack | null = null // fabricTrack 自定义摄像头
  public localMainVideoTrack: ICameraVideoTrack | ILocalVideoTrack | null = null
  public localViceVideoTrack: ICameraVideoTrack | ILocalVideoTrack | [ILocalVideoTrack, ILocalAudioTrack] | null = null
  public localAudioTrack: IMicrophoneAudioTrack | null = null
  public remoteVideoTrack: IAgoraRTCRemoteUser[] = []
  public remoteAudioTrack: IAgoraRTCRemoteUser[] = []
  public mainClient: IAgoraRTCClient | null = null
  public viceClient: IAgoraRTCClient | null = null
  // 矫正
  public mainCorrect: Correct = 1
  public viceCorrect: Correct = 1
  // 设备ID
  public mainCameraDeviceId: string = ''
  public viceCameraDeviceId: string = ''
  public microphoneDeviceId: string = ''
  public animationFrameId: number | null = null

  constructor(appId: string) {
    // AgoraRTC.enableLogUpload()
    // AgoraRTC.setLogLevel(1)
    super()
    this.appId = appId
  }

  public async init(options: RtcOptions) {
    const { channel, token, viceToken, uid } = options
    this.channel = channel
    this.token = token || null
    this.viceToken = viceToken || null
    this.uid = uid
    this.mainUid = Number(`10${this.uid}`)
    this.viceUid = Number(`11${this.uid}`)
    this.mainClient = AgoraRTC.createClient(clientConfig)
    this.viceClient = AgoraRTC.createClient(clientConfig)
    this.subscribeClientEvents(this.mainClient)
    await this.join(this.mainUid, this.mainClient, this.token)
    await this.join(this.viceUid, this.viceClient, this.viceToken)
  }

  subscribeClientEvents(client: IAgoraRTCClient) {
    // 开启音量变化
    client.enableAudioVolumeIndicator()
    // 推流
    client.on('user-published', this.userPublished.bind(this))
    // 取消推流
    client.on('user-unpublished', this.userUnpublished.bind(this))
    // 音量变化
    client.on('volume-indicator', this.volumeIndicator.bind(this))
    // 播放状态发生变化
    client.on('stream-inject-status', this.playerStatusChange.bind(this))
    // 加入频道
    client.on('user-joined', this.userJoined.bind(this))
    // 离开频道
    client.on('user-left', this.userLeft.bind(this))
    // 错误
    client.on('error', this.error.bind(this))
    // 连接发生改变
    client.on('connection-state-change', this.connectionStateShange.bind(this))
    // 网络
    client.on('network-quality', (e) => {
      super.emit('network-quality', {
        local: e,
        remote: client.getRemoteNetworkQuality()
      })
    })
    // 音视频开关
    client.on('unmute-video', (e: { uid: UID }) => super.emit('unmute-video', 'video', { uid: e.uid }))
    client.on('unmute-audio', (e: { uid: UID }) => super.emit('unmute-audio', 'audio', { uid: e.uid }))
    client.on('mute-video', (e: { uid: UID }) => super.emit('mute-video', 'video', { uid: e.uid }, 2))
    client.on('mute-audio', (e: { uid: UID }) => super.emit('mute-audio', 'audio', { uid: e.uid }))
  }

  public async join(uid: number, client: IAgoraRTCClient, token: string | null) {
    try {
      await client.join(this.appId, this.channel, token, uid)
      super.emit('join-success', uid)
    } catch (e) {
      super.emit('join-fail', e)
      return Promise.reject(e)
    }
  }

  public async publish(type: ActMediaType = 'all') {
    let tracks: ILocalTrack[] = []

    switch (type) {
      case 'mainVideo':
        if (this.localMainVideoTrack) {
          await this.mainClient?.publish(this.localMainVideoTrack)
        }
        break
      case 'viceVideo':
        if (this.localViceVideoTrack) {
          await this.viceClient?.publish(this.localViceVideoTrack)
        }
        break
      case 'viceScreen':
        if (this.localViceVideoTrack) {
          await this.viceClient?.publish(this.localViceVideoTrack)
        }
        break
      case 'audio':
        if (this.localAudioTrack) {
          await this.mainClient?.publish(this.localAudioTrack)
        }
        break
      default:
        tracks = [this.localMainVideoTrack, this.localAudioTrack].filter((v) => v !== null) as ILocalTrack[]
        if (tracks.length) {
          await this.mainClient?.publish(tracks)
        }
        if (this.localViceVideoTrack) {
          await this.viceClient?.publish(this.localViceVideoTrack)
        }
        break
    }
  }

  public async unpublish(type: ActMediaType = 'all') {
    let tracks: ILocalTrack[] = []

    switch (type) {
      case 'mainVideo':
        if (this.localMainVideoTrack) {
          await this.mainClient?.unpublish(this.localMainVideoTrack)
        }
        break
      case 'mainScreen':
        if (this.localMainVideoTrack) {
          await this.mainClient?.unpublish(this.localMainVideoTrack)
        }
        break
      case 'viceVideo':
        if (this.localViceVideoTrack) {
          await this.viceClient?.unpublish(this.localViceVideoTrack)
        }
        break
      case 'viceScreen':
        if (this.localViceVideoTrack) {
          await this.viceClient?.unpublish(this.localViceVideoTrack)
        }
        break
      case 'audio':
        if (this.localAudioTrack) {
          await this.mainClient?.unpublish(this.localAudioTrack)
        }
        break
      default:
        tracks = [this.localMainVideoTrack, this.localAudioTrack].filter((v) => v !== null) as ILocalTrack[]
        if (tracks.length) {
          await this.mainClient?.unpublish(tracks)
        }
        if (this.localViceVideoTrack) {
          await this.viceClient?.unpublish(this.localViceVideoTrack)
        }
        break
    }
  }

  public async play(track: any, element: HTMLElement, config?: PlayConf, uid?: number) {
    const { fit = 'contain', mirror = true } = config || {}
    track.play(element, { fit, mirror })
  }

  public async leave() {
    await this.mainClient?.leave()
    await this.viceClient?.leave()
  }

  // 销毁
  public async destroy() {
    // 移除所有监听事件
    this.mainClient?.removeAllListeners()
    this.viceClient?.removeAllListeners()
    // 退出教室内移除设备热插拔监听
    AgoraRTC.onMicrophoneChanged = undefined
    AgoraRTC.onCameraChanged = undefined
    AgoraRTC.onPlaybackDeviceChanged = undefined

    // 取消动画帧
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }

    // 销毁本地音视频轨道。
    this.localAudioTrack?.close()
    this.localMainVideoTrack?.close()

    if (this.localMainVideoTrack) {
      this.localMainVideoTrack.close()
    }

    if (this.localViceVideoTrack) {
      if (Array.isArray(this.localViceVideoTrack)) {
        this.localViceVideoTrack.forEach((track) => track.close())
      } else {
        this.localViceVideoTrack.close()
      }
    }

    // 销毁fabricTrack
    this.mainFabricTrack?.destroy()
    this.viceFabricTrack?.destroy()

    // 清空数据
    this.localMainVideoTrack = null
    this.localViceVideoTrack = null
    this.localAudioTrack = null
    this.remoteVideoTrack = []
    this.remoteAudioTrack = []

    // 离开频道。
    await this.leave()
  }

  async userPublished(remoteUser: IAgoraRTCRemoteUser, mediaType: MediaType) {
    console.info(' --------- 连接远端流 --------- ', remoteUser, mediaType)
    if (remoteUser.uid !== this.viceUid) {
      await this.mainClient?.subscribe(remoteUser, mediaType)
      if (mediaType === 'audio') {
        remoteUser.audioTrack?.play()
        this.remoteAudioTrack.push(remoteUser)
      } else {
        this.remoteVideoTrack.push(remoteUser)
      }
      // tips: 在收到用户加入才初始化界面的情况下，收流回调需要延迟，避免监听错过
      setTimeout(() => {
        super.emit('user-published', mediaType, remoteUser)
      }, 1000)
    }
  }

  async userUnpublished(remoteUser: IAgoraRTCRemoteUser, mediaType: MediaType) {
    console.info(' --------- 远端流断开 --------- ', remoteUser, mediaType)
    await this.mainClient?.unsubscribe(remoteUser, mediaType)
    if (mediaType === 'audio') {
      remoteUser.audioTrack?.play()
      const index = this.remoteAudioTrack.findIndex((v) => v.uid === remoteUser.uid)
      if (index >= 0) {
        this.remoteAudioTrack.splice(index, 1)
      }
    } else {
      const index = this.remoteVideoTrack.findIndex((v) => v.uid === remoteUser.uid)
      if (index >= 0) {
        this.remoteVideoTrack.splice(index, 1)
      }
    }
    super.emit('user-unpublished', mediaType, remoteUser)
  }

  volumeIndicator(volumes: VolumeType[]) {
    super.emit('volume-indicator', volumes)
  }

  playerStatusChange() {}

  userJoined(user: IAgoraRTCRemoteUser) {
    console.log('Agora userJoined', user.uid)
    super.emit('user-add', user.uid.toString())
  }

  userLeft(user: IAgoraRTCRemoteUser) {
    console.log('Agora userLeft', user.uid)
    super.emit('user-leave', user.uid.toString())
  }

  error() {}

  connectionStateShange(curState: ConnectionState, revState: ConnectionState, reason?: ConnectionDisconnectedReason) {
    // SDK 和服务器有以下 5 种连接状态:
    //   1."DISCONNECTED": 连接断开。该状态表示用户处于以下任一阶段：
    //     尚未通过 join 加入频道。
    //     已经通过 leave 离开频道。
    //     被踢出频道或者连接失败等异常情况。
    //   2."CONNECTING": 正在连接中。当调用 join 时为此状态。
    //   3."CONNECTED": 已连接。该状态表示用户已经加入频道，可以在频道内发布或订阅媒体流。
    //   4."RECONNECTING": 正在重连中。因网络断开或切换而导致 SDK 与服务器的连接中断，SDK 会自动重连，此时连接状态变为 "RECONNECTING"。
    //   5."DISCONNECTING": 正在断开连接。在调用 leave 的时候为此状态。
    console.info('RTC connection-state', curState, revState, reason)
    super.emit('connection-state', curState, revState, reason)
  }

  /**
   * 设置镜像信息
   * @param type 主副
   * @param status 矫正类型
   */
  public setCorrect(type: CorrectType, status: Correct) {
    let fabricTrack: FabricTrack | null = null
    if (type === 'main') {
      this.mainCorrect = status
      fabricTrack = this.mainFabricTrack
    }
    if (type === 'vice') {
      this.viceCorrect = status
      fabricTrack = this.viceFabricTrack
    }
    if (fabricTrack) {
      fabricTrack.setCorrect(status)
    }
  }

  async changeMainCamera({ deviceId, publish = true, canvas = false, screen = null, status = 1 }: ChangeCamera) {
    console.info('ChangeMainCamera', deviceId, publish, canvas, status)
    try {
      this.mainCameraDeviceId = deviceId
      if (this.localMainVideoTrack) {
        if (deviceId) {
          if (!this.mainFabricTrack) {
            await (this.localMainVideoTrack as ICameraVideoTrack).setDevice(deviceId)
          }
          if (this.mainFabricTrack) {
            this.mainFabricTrack.setDevice(deviceId)
          }
          toast.success('切换主摄像头成功')
        } else {
          const localVideoTracks = this.mainClient?.localTracks.filter((v) => v.trackMediaType === 'video')
          if (localVideoTracks && localVideoTracks.length) {
            if (screen) {
              await this.unpublish('mainScreen')
            } else {
              await this.unpublish('mainVideo')
              toast.success('关闭主摄像头成功')
            }
          }
          if (this.localMainVideoTrack) {
            if (Array.isArray(this.localMainVideoTrack)) {
              this.localMainVideoTrack.forEach((track) => track.close())
            } else {
              this.localMainVideoTrack.close()
            }
            this.localMainVideoTrack = null
            if (this.mainFabricTrack) {
              this.mainFabricTrack.destroy()
              this.mainFabricTrack = null
            }
          }
          super.emit('media-close', 'video', { uid: this.mainUid })
        }
      } else if (deviceId) {
        if (canvas) {
          this.mainCorrect = status
          this.mainFabricTrack = new FabricTrack({ deviceId, correct: this.mainCorrect, width: videSize.width, height: videSize.height })
          this.localMainVideoTrack = this.mainFabricTrack.localVideoTrack
        } else if (screen) {
          // try {
          if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            const root = document.getElementById('root') as HTMLElement
            const getAmplifier = (dom: HTMLElement, rate: number = 5) => {
              const result: any = {}
              const items: any = dom.getBoundingClientRect()
              Object.keys(JSON.parse(JSON.stringify(items))).forEach((item) => {
                result[item] = items[item] * rate
              })
              return result
            }
            const rootRect = getAmplifier(root)

            const mediaStream = await navigator.mediaDevices.getUserMedia({
              audio: false,
              video: {
                mandatory: {
                  chromeMediaSource: 'desktop',
                  chromeMediaSourceId: deviceId,
                  minWidth: rootRect.width,
                  maxWidth: rootRect.width,
                  minHeight: rootRect.height,
                  maxHeight: rootRect.height
                } as any
              } as any
            })

            const customVideoTrackConfig = {
              mediaStreamTrack: mediaStream.getVideoTracks()[0],
              bitrateMax: 3000,
              bitrateMin: 1500,
              frameRate: {
                min: 24,
                max: 60
              },
              width: {
                max: rootRect.width,
                min: rootRect.width,
                ideal: rootRect.width
              },
              height: {
                max: rootRect.height,
                min: rootRect.height,
                ideal: rootRect.height
              }
            }
            const WebBrowser = document.getElementById('WebBrowser') as HTMLElement
            //  获取浏览器dom
            if (WebBrowser && !WebBrowser.className.includes('hidden')) {
              const target = document.getElementById('WebBrowser') as HTMLElement

              const targetRect = getAmplifier(target)
              // console.log('🚀 ~ Agora ~ changeMainCamera ~ targetRect:', targetRect)
              // 创建Canvas用于裁剪视频
              const canvas = document.createElement('canvas')
              canvas.width = targetRect.width * 2
              canvas.height = targetRect.height * 2
              const ctx = canvas.getContext('2d') as CanvasRenderingContext2D

              // 创建Canvas的MediaStream
              const canvasStream = canvas.captureStream(30) // 30fps
              customVideoTrackConfig.mediaStreamTrack = canvasStream?.getVideoTracks()[0]
              customVideoTrackConfig.width = {
                max: targetRect.width,
                min: targetRect.width,
                ideal: targetRect.width
              }
              customVideoTrackConfig.height = {
                max: targetRect.height,
                min: targetRect.height,
                ideal: targetRect.height
              }

              // 创建视频元素并设置源
              const videoElement = document.createElement('video')
              videoElement.srcObject = mediaStream
              videoElement.muted = true
              videoElement.width = rootRect.width
              videoElement.height = rootRect.height

              // 开始绘制Canvas
              const drawFrame = () => {
                if (!mediaStream || !videoElement) return

                // 如果视频未加载，尝试加载
                if (videoElement.readyState < 2) {
                  videoElement.play().catch((err) => {
                    console.error('视频播放错误:', err)
                  })
                }
                // 获取目标元素的位置
                const targetRect = getAmplifier(target)
                // console.log('🚀 ~ Agora ~ changeMainCamera ~ drawFrame:', targetRect)

                // 绘制视频帧到Canvas
                if (videoElement.readyState >= 2) {
                  // 确保视频已加载
                  ctx.drawImage(videoElement, targetRect.x, targetRect.y, targetRect.width, targetRect.height, 0, 0, canvas.width, canvas.height)
                }

                // 继续下一帧
                this.animationFrameId = requestAnimationFrame(drawFrame)
              }

              // 开始绘制循环
              drawFrame()
            }

            this.localMainVideoTrack = await AgoraRTC.createCustomVideoTrack(customVideoTrackConfig)
          }
          // } catch (nativeError) {
          //   console.error('原生getUserMedia方法失败:', nativeError)
          // }
        } else {
          this.localMainVideoTrack = await AgoraRTC.createCameraVideoTrack({ ...cameraVideoTrackInitConfig, cameraId: deviceId })
        }
        if (publish) {
          screen ? this.publish('mainScreen') : this.publish('mainVideo')
        }
        super.emit('media-ready', 'video', { uid: this.mainUid, videoTrack: this.localMainVideoTrack })
      }
    } catch (e) {
      console.info(e)
      // toast.error('切换主摄像头失败')
      return Promise.reject(e)
    }
  }

  async changeViceCamera({ deviceId, publish = true, canvas = false, screen = null, status = 1 }: ChangeCamera) {
    console.info('ChangeViceCamera', deviceId, publish, canvas, screen, status)
    try {
      this.viceCameraDeviceId = deviceId
      if (this.localViceVideoTrack) {
        if (deviceId) {
          if (!this.viceFabricTrack) {
            await (this.localViceVideoTrack as ICameraVideoTrack).setDevice(deviceId)
          }
          if (this.viceFabricTrack) {
            this.viceFabricTrack.setDevice(deviceId)
          }
          toast.success('切换副摄像头成功')
        } else {
          const localVideoTracks = this.viceClient?.localTracks.filter((v) => v.trackMediaType === 'video')

          if (localVideoTracks && localVideoTracks.length) {
            if (screen) {
              await this.unpublish('viceScreen')
            } else {
              await this.unpublish('viceVideo')
              toast.success('关闭副摄像头成功')
            }
          }

          if (this.localViceVideoTrack) {
            if (Array.isArray(this.localViceVideoTrack)) {
              this.localViceVideoTrack.forEach((track) => track.close())
            } else {
              this.localViceVideoTrack.close()
            }
            this.localViceVideoTrack = null
            if (this.viceFabricTrack) {
              this.viceFabricTrack.destroy()
              this.viceFabricTrack = null
            }
          }
          super.emit('media-close', 'video', { uid: this.viceUid })
        }
      } else if (deviceId) {
        if (canvas) {
          this.viceCorrect = status
          this.viceFabricTrack = new FabricTrack({ deviceId, correct: this.viceCorrect, width: videSize.width, height: videSize.height })
          this.localViceVideoTrack = this.viceFabricTrack.localVideoTrack
        } else if (screen?.bounds) {
          try {
            if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
              const { width, height } = screen.bounds
              const mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: {
                  mandatory: {
                    chromeMediaSource: 'desktop',
                    chromeMediaSourceId: deviceId,
                    minWidth: width,
                    maxWidth: width,
                    minHeight: height,
                    maxHeight: height
                  } as any
                } as any
              })
              this.localViceVideoTrack = await AgoraRTC.createCustomVideoTrack({
                mediaStreamTrack: mediaStream.getVideoTracks()[0]
              })
            }
          } catch (nativeError) {
            console.error('原生getUserMedia方法失败:', nativeError)
          }
        } else {
          this.localViceVideoTrack = await AgoraRTC.createCameraVideoTrack({ ...cameraVideoTrackInitConfig, cameraId: deviceId })
        }
        if (publish) {
          screen ? this.publish('viceScreen') : this.publish('viceVideo')
        }
        super.emit('media-ready', 'video', { uid: this.viceUid, videoTrack: this.localViceVideoTrack })
      }
    } catch (e) {
      console.info(e)
      // toast.error('切换副摄像头失败')
      return Promise.reject(e)
    }
  }

  async changeMicrophone({ deviceId, publish = true }: ChangeMicrophone) {
    console.info('ChangeMicrophone', deviceId, publish)
    try {
      this.microphoneDeviceId = deviceId
      if (this.localAudioTrack) {
        if (deviceId) {
          await this.localAudioTrack.setDevice(deviceId)
          toast.success('切换麦克风成功')
        } else {
          const localVideoTracks = this.mainClient?.localTracks.filter((v) => v.trackMediaType === 'audio')
          if (localVideoTracks && localVideoTracks.length) {
            this.unpublish('audio')
          }
          if (this.localAudioTrack) {
            this.localAudioTrack.close()
            this.localAudioTrack = null
          }
          super.emit('media-close', 'audio', { uid: this.mainUid })
          toast.success('关闭麦克风成功')
        }
      } else if (deviceId) {
        const localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
          AEC: true,
          AGC: true,
          ANS: true,
          microphoneId: deviceId
        })
        this.localAudioTrack = localAudioTrack
        if (publish) {
          this.publish('audio')
        }
        super.emit('media-ready', 'audio', { uid: this.mainUid })
      }
    } catch (e) {
      console.info(e)
      // toast.error('切换麦克风失败')
      return Promise.reject(e)
    }
  }

  async deviceOn(type: 'mainVideo' | 'viceVideo' | 'audio') {
    switch (type) {
      case 'mainVideo':
        if (!this.localMainVideoTrack) {
          return Promise.reject('摄像头为空，请查看设备')
        }
        await this.localMainVideoTrack?.setEnabled(true)
        super.emit('unmute-video', 'video', { uid: this.mainUid, videoTrack: this.localMainVideoTrack })
        break
      case 'viceVideo':
        if (!this.localViceVideoTrack) {
          return Promise.reject('摄像头为空，请查看设备')
        }
        if (Array.isArray(this.localViceVideoTrack)) {
          // 如果是数组（可能是屏幕共享的视频和音频轨道）
          await Promise.all(this.localViceVideoTrack.map((track) => (track.trackMediaType === 'video' ? track.setEnabled(true) : Promise.resolve())))
        } else {
          await this.localViceVideoTrack?.setEnabled(true)
        }
        super.emit('unmute-video', 'video', { uid: this.viceUid, videoTrack: this.localViceVideoTrack })
        break
      case 'audio':
        if (!this.localAudioTrack) {
          return Promise.reject('麦克风为空，请查看设备')
        }
        await this.localAudioTrack?.setEnabled(true)
        super.emit('unmute-audio', 'audio', { uid: this.mainUid })
        break
      default:
        break
    }
  }

  async deviceOff(type: 'mainVideo' | 'viceVideo' | 'audio') {
    switch (type) {
      case 'mainVideo':
        if (!this.localMainVideoTrack) {
          return Promise.reject('摄像头为空，请查看设备')
        }
        await this.localMainVideoTrack?.setEnabled(false)
        super.emit('mute-video', 'video', { uid: this.mainUid }, 2)
        break
      case 'viceVideo':
        if (!this.localViceVideoTrack) {
          return Promise.reject('摄像头为空，请查看设备')
        }
        if (Array.isArray(this.localViceVideoTrack)) {
          // 如果是数组（可能是屏幕共享的视频和音频轨道）
          await Promise.all(this.localViceVideoTrack.map((track) => (track.trackMediaType === 'video' ? track.setEnabled(false) : Promise.resolve())))
        } else {
          await this.localViceVideoTrack?.setEnabled(false)
        }
        super.emit('mute-video', 'video', { uid: this.viceUid }, 2)
        break
      case 'audio':
        if (!this.localAudioTrack) {
          return Promise.reject('麦克风为空，请查看设备')
        }
        await this.localAudioTrack?.setEnabled(false)
        super.emit('mute-audio', 'audio', { uid: this.mainUid })
        break
      default:
        break
    }
  }
  public static getCameras(): Promise<MediaDeviceInfo[]> {
    return AgoraRTC.getCameras()
  }

  public static getMicrophones(): Promise<MediaDeviceInfo[]> {
    return AgoraRTC.getMicrophones()
  }

  public static getPlaybacks(): Promise<MediaDeviceInfo[]> {
    return AgoraRTC.getPlaybackDevices()
  }

  public static async getSettingDevices() {
    return AgoraRTC.getDevices().then((res) => {
      const videoinput: MediaDeviceInfo[] = []
      const audioinput: MediaDeviceInfo[] = []
      const audiooutput: MediaDeviceInfo[] = []
      res.forEach((device) => {
        if (device.kind === 'videoinput') {
          videoinput.push(device)
        }
        if (device.kind === 'audioinput') {
          audioinput.push(device)
        }
        if (device.kind === 'audiooutput') {
          audiooutput.push(device)
        }
      })
      return [
        {
          key: 'mainCamera',
          title: '主镜头',
          subTitle: '(人脸)',
          devices: videoinput
        },
        {
          key: 'viceCamera',
          title: '副镜头',
          subTitle: '(画纸)',
          devices: videoinput
        },
        {
          key: 'microphone',
          title: '麦克风',
          subTitle: '',
          devices: audioinput
        },
        {
          key: 'playback',
          title: '扬声器',
          subTitle: '',
          devices: audiooutput
        }
      ]
    })
  }
}
